import { useCallback } from 'react';
import { StateManager } from '../managers/StateManager';
import { StorageManager } from '../managers/StorageManager';
import { logger } from '../utils/logger';

export const useStorage = (stateManager: StateManager) => {
  // Auto-save on state changes
  stateManager.subscribe((state) => {
    StorageManager.save(state);
  });

  // Load saved data on initialization
  const loadSavedData = useCallback(() => {
    logger.storage.debug('loadSavedData() called');
    const savedState = StorageManager.load();
    if (savedState) {
      logger.storage.info('Loaded saved state', { 
        connectors: savedState.connectors.length, 
        nodes: savedState.nodes.length, 
        wires: savedState.wires.length 
      });
      stateManager.loadState(savedState);
    } else {
      logger.storage.debug('No saved state found');
    }
  }, [stateManager]);

  const exportData = useCallback(() => {
    const state = stateManager.getState();
    StorageManager.export(state);
  }, [stateManager]);

  const importData = useCallback(async (file: File) => {
    try {
      const importedState = await StorageManager.import(file);
      stateManager.loadState(importedState);
      logger.storage.info('Data imported successfully');
    } catch (error) {
      logger.storage.error('Import failed', error);
      alert('Failed to import file: ' + (error as Error).message);
    }
  }, [stateManager]);

  const clearAllData = useCallback(() => {
    if (window.confirm('Are you sure? This will delete all connectors, wires, and nodes permanently.')) {
      stateManager.setState({
        connectors: [],
        nodes: [],
        wires: [],
        connectorTypes: [],
        segments: [],
        background: undefined
      });
      StorageManager.clear();
      logger.storage.info('All data cleared');
    }
  }, [stateManager]);

  return {
    loadSavedData,
    exportData,
    importData,
    clearAllData
  };
};